import { useCallback, useEffect, useRef } from "react";
import type { ZoomState } from "../../types";
import type { UseAnimatedZoomArgs, UseAnimatedZoomResult } from "./types";

const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3;

const interpolate = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress;

const isSameZoomState = (first: ZoomState, second: ZoomState): boolean =>
  first.zoomLevel === second.zoomLevel &&
  first.panOffset.x === second.panOffset.x &&
  first.panOffset.y === second.panOffset.y;

export const useAnimatedZoom = ({
  durationMs,
  getZoomState,
  applyZoomState,
}: UseAnimatedZoomArgs): UseAnimatedZoomResult => {
  const animationFrameIdRef = useRef<number | null>(null);
  const targetZoomStateRef = useRef<ZoomState | null>(null);

  const cancelZoomAnimation = useCallback(() => {
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }
    animationFrameIdRef.current = null;
    targetZoomStateRef.current = null;
  }, []);

  const animateZoomTo = useCallback(
    (targetZoomState: ZoomState) => {
      cancelZoomAnimation();

      const startZoomState = getZoomState();

      if (durationMs <= 0 || isSameZoomState(startZoomState, targetZoomState)) {
        applyZoomState(targetZoomState);
        return;
      }

      const startTimestamp = performance.now();
      targetZoomStateRef.current = targetZoomState;

      const renderAnimationFrame = (timestamp: number) => {
        const elapsedMs = Math.max(timestamp - startTimestamp, 0);
        const progress = Math.min(elapsedMs / durationMs, 1);

        if (progress === 1) {
          animationFrameIdRef.current = null;
          targetZoomStateRef.current = null;
          applyZoomState(targetZoomState);
          return;
        }

        const easedProgress = easeOutCubic(progress);
        applyZoomState({
          zoomLevel: interpolate(
            startZoomState.zoomLevel,
            targetZoomState.zoomLevel,
            easedProgress,
          ),
          panOffset: {
            x: interpolate(
              startZoomState.panOffset.x,
              targetZoomState.panOffset.x,
              easedProgress,
            ),
            y: interpolate(
              startZoomState.panOffset.y,
              targetZoomState.panOffset.y,
              easedProgress,
            ),
          },
        });
        animationFrameIdRef.current =
          requestAnimationFrame(renderAnimationFrame);
      };

      animationFrameIdRef.current = requestAnimationFrame(renderAnimationFrame);
    },
    [durationMs, getZoomState, applyZoomState, cancelZoomAnimation],
  );

  const getSettledZoomState = useCallback(
    () => targetZoomStateRef.current ?? getZoomState(),
    [getZoomState],
  );

  useEffect(() => cancelZoomAnimation, [cancelZoomAnimation]);

  const animatedZoom: UseAnimatedZoomResult = {
    animateZoomTo,
    cancelZoomAnimation,
    getSettledZoomState,
  };

  return animatedZoom;
};
